import { useCallback } from 'react';
import { API_URL } from '../config/env';
import { useValidatedTenantId } from './auth/use-validated-tenant-id';
import { useSse } from './use-sse';

export interface PreviewReadyEvent {
  type: 'preview-ready';
  tenantId: string;
  sourceKey: string;
  result: {
    sourceKey: string;
    pages: Array<{ pageNumber: number; storageKey: string; width: number; height: number }>;
    totalPages: number;
  };
}

export interface PreviewErrorEvent {
  type: 'preview-error';
  tenantId: string;
  sourceKey: string;
  error: string;
}

type PreviewEvent = PreviewReadyEvent | PreviewErrorEvent;

interface UsePreviewSseOptions {
  onPreviewReady?: (event: PreviewReadyEvent) => void;
  onPreviewError?: (event: PreviewErrorEvent) => void;
  enabled?: boolean;
}

export function usePreviewSse(options: UsePreviewSseOptions) {
  const { onPreviewReady, onPreviewError, enabled = true } = options;
  const tenantId = useValidatedTenantId();

  // EventSource non puo' mandare l'header `x-tenant-id`: senza il fallback
  // `tenant_id` in query ClerkAuthGuard risponde 401 tenantHeaderMissing e
  // l'evento preview-ready non arriva mai (anteprima bloccata sullo spinner).
  const getUrl = useCallback(() => {
    if (!tenantId) {
      return null;
    }
    const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    return `${base}/previews/stream?tenant_id=${encodeURIComponent(tenantId)}`;
  }, [tenantId]);

  const handleEvent = useCallback(
    (data: PreviewEvent) => {
      if (data.type === 'preview-ready') {
        onPreviewReady?.(data);
      } else if (data.type === 'preview-error') {
        onPreviewError?.(data);
      }
    },
    [onPreviewReady, onPreviewError],
  );

  return useSse<PreviewEvent>({
    getUrl,
    enabled,
    onEvent: handleEvent,
  });
}
